/**
 * Format a timestamp to a readable date string
 */
export function formatDate(timestamp) {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/**
 * Format a timestamp to a readable date and time string
 */
export function formatDateTime(timestamp) {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Format a timestamp to relative time (e.g., "2 hours ago")
 */
export function formatTimeAgo(timestamp) {
    if (!timestamp) return 'N/A';

    const now = Date.now();
    const diff = now - timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years}y ago`;
    if (months > 0) return `${months}mo ago`;
    if (weeks > 0) return `${weeks}w ago`;
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
}

/**
 * Format a number with commas
 */
export function formatNumber(num) {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
}

/**
 * Truncate text to a specified length
 */
export function truncate(text, length = 50) {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
}

/**
 * Get account type badge variant - matches mobile app colors
 * verified: blue, reporter: teal/cyan, community: purple, special: yellow, super: blue
 */
export function getAccountTypeBadgeVariant(accountType) {
    const variants = {
        verified: 'info',      // blue
        reporter: 'reporter',  // teal/cyan (#89C2CB)
        community: 'purple',   // purple (#605CFF)
        special: 'warning',    // yellow
        super: 'info',         // blue
    };
    return variants[accountType] || 'primary';
}

/**
 * Get account type display name
 */
export function getAccountTypeLabel(accountType) {
    const labels = {
        verified: 'Verified',
        reporter: 'Reporter',
        community: 'Community',
        special: 'Special',
        super: 'Super',
    };
    return labels[accountType] || accountType;
}

/**
 * Get privilege display name
 */
export function getPrivilegeLabel(privilege) {
    const labels = {
        super_admin: 'Super Admin',
        user_management: 'User Management',
        content_moderation: 'Content Moderation',
        verification_approval: 'Verification Approval',
        analytics_view: 'Analytics View',
        system_config: 'System Configuration',
        basketball_management: 'Basketball Management',
    };
    return labels[privilege] || privilege;
}

/**
 * Download data as CSV
 */
export function downloadCSV(data, filename) {
    if (!data || data.length === 0) return;

    // Get headers from first object
    const headers = Object.keys(data[0]);

    // Create CSV content
    let csv = headers.join(',') + '\n';

    data.forEach(row => {
        const values = headers.map(header => {
            const value = row[header];
            // Escape commas and quotes
            const escaped = String(value).replace(/"/g, '""');
            return `"${escaped}"`;
        });
        csv += values.join(',') + '\n';
    });

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
}

/**
 * Validate email
 */
export function isValidEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
}

/**
 * Validate admin email (Gmail or LBA domain)
 */
export function isValidAdminEmail(email) {
    if (!isValidEmail(email)) return false;
    const allowedDomains = ['@gmail.com', '@lba.me'];
    return allowedDomains.some(domain => email.endsWith(domain));
}

// Keep old function for backwards compatibility
export function isValidGmail(email) {
    return isValidAdminEmail(email);
}

/**
 * Get status badge variant
 */
export function getStatusBadgeVariant(status) {
    const variants = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger',
        active: 'success',
        inactive: 'danger',
        resolved: 'success',
        dismissed: 'primary',
    };
    return variants[status] || 'primary';
}

